import React from 'react';
import ReactDOM from 'react-dom/client';
import axios from 'axios';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

// ── Clasificación de endpoints ───────────────────────────────────────────────
// Es el único lugar donde se decide qué sesión corresponde a cada petición. Las
// pantallas no arman la cabecera Authorization: si lo hicieran, habría dos
// fuentes de verdad y una terminaría desactualizada respecto de la otra.

// Se consumen sin sesión: login, refresco, recuperación de contraseña, registro
// y el rescate del admin por código de emergencia.
const SIN_SESION = [
  '/api/v1/auth/token/',
  '/api/v1/auth/ferreteria-login/',
  '/api/v1/auth/password-reset/',
  '/api/v1/auth/registro/',
  '/api/v1/auth/admin/recover/',
];

const sinSesion = (url) => SIN_SESION.some(ruta => url.includes(ruta));

// Pertenecen a la plataforma y usan la sesión de superadmin. Incluye las rutas
// bajo /auth/admin/ que exigen estar autenticado como admin: dejarlas fuera era
// el origen de que esas peticiones viajaran sin token.
const esDePlataforma = (url) =>
  url.includes('/api/v1/public/') || url.includes('/api/v1/auth/admin/');

// ── Interceptor de REQUEST: inyecta el token correcto según URL ───────────────
axios.interceptors.request.use(config => {
  const url = config.url || '';
  if (!sinSesion(url)) {
    const token = esDePlataforma(url)
      ? localStorage.getItem('admin_access_token')
      : sessionStorage.getItem('access_token');
    if (token) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${token}`;
    } else {
      delete config.headers?.['Authorization'];
    }
  }
  return config;
}, error => Promise.reject(error));

// ── Interceptor global de RESPONSE 401 ───────────────────────────────────────
// Los endpoints de login devuelven 401 por credenciales incorrectas → no redirigir.
// Para el resto, limpia SOLO los tokens del tipo de sesión afectada.
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';

      // Login endpoints: dejar que el componente maneje el error
      if (url.includes('/auth/token/') || url.includes('/auth/ferreteria-login/')) {
        return Promise.reject(error);
      }

      // Se clasifica con el mismo criterio que la inyección del token. Antes se
      // miraba solo /api/v1/public/, así que un 401 en una ruta de admin bajo
      // /auth/admin/ borraba la sesión de tenant y mandaba al login de la tienda.
      if (esDePlataforma(url)) {
        localStorage.removeItem('admin_access_token');
        localStorage.removeItem('admin_refresh_token');
        localStorage.removeItem('user_type');
        window.location.replace('/admin/login');
      } else {
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('refresh_token');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('user_type');
        sessionStorage.removeItem('tenant_name');
        window.location.replace('/login');
      }
      // Promesa que nunca resuelve: evita que el catch del componente muestre "Error al xxx"
      return new Promise(() => {});
    }
    return Promise.reject(error);
  }
);

// ── Sincronización de logout entre pestañas (solo admin) ─────────────────────
// Los tokens de tenant están en sessionStorage, que es por pestaña y no dispara
// el evento 'storage' entre tabs. Solo se sincroniza el logout del superadmin.
window.addEventListener('storage', (event) => {
  if (event.key === 'admin_access_token' && event.newValue === null && event.oldValue) {
    const onAdminPage = window.location.pathname.startsWith('/admin');
    if (onAdminPage && !window.location.pathname.endsWith('login')) {
      window.location.replace('/admin/login');
    }
  }
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

reportWebVitals();
